/**
 * eligibility.js — WHO can do this work (CT-025 R2). The seed of the powerful assignment algorithm.
 *
 * When a milestone has no owner (or must move off an over-committed person), the system shouldn't
 * just pick whoever is least busy — it should pick someone ELIGIBLE: the right skill/role AND the
 * availability. This module scores candidates on both and explains why, so the cockpit can recommend
 * a specific, defensible assignment.
 *
 * It works off data we already have and degrades gracefully:
 *   - REQUIRED skill is inferred from the milestone's name via keyword rules (no new field needed).
 *   - A person's skills come from their Contact "Practice Roles" (already loaded) PLUS an editable
 *     Supabase `member_skills` layer that refines/overrides over time.
 *   - If nobody has a skill match (data still sparse), it falls back to availability-only — the same
 *     behavior as before — so it is useful from day one and gets smarter as skills are entered.
 *
 * PURE (no DOM, no services) so it is node-testable. The keyword rules are the first "lever" set and
 * will move into the tunable scheduler config later.
 */

// Milestone-name keyword → required skill tag. Order independent; a name can require several. These
// are the PRODUCTION skills a milestone can require; business/leadership skills are role-assigned and
// rarely "required" by a milestone (so they're not inferred here — they come from roles + the broader
// attribution map used by the skill-backfill job).
const SKILL_RULES = [
  [/\bbim\b|3d model|as-?built|existing (building|conditions)|point cloud|laser scan|revit/i, 'BIM'],
  [/permit|plan check|discretionary|entitlement|zoning|agency|dapc|ppc/i, 'Permitting'],
  [/structural|\bstruct\b/i,                                    'Structural'],
  [/landscape|\bcivil\b|\bsite\b|grading|survey/i,              'Site Design'],
  [/interior/i,                                                 'Interiors'],
  [/concept|schematic|design development|owner criteria|\bsd\b|\bdd\b/i, 'Design'],
  [/construction|\brfi\b|submittal|\basi\b|\bbid\b|\bca\b/i,     'Construction'],
  [/drawing|sheet|construction document|\bcd\b|delta|revision/i, 'Drafting'],
  [/\bqc\b|quality|back-?check|redline|markup/i,                'Quality Control'],
  [/client|owner meeting|presentation|client review/i,          'Client Management'],
  [/project management|kickoff|\bpm\b/i,                        'Project Management'],
]

/**
 * Canonical skill tags — the full assortment a healthy practice needs, not just craft. Grouped:
 *   Production: Design, Drafting, BIM, Interiors, Structural, Site Design, Construction, Permitting
 *   Delivery:   Project Management, Quality Control
 *   Growth:     Client Management, Business Development, Marketing
 *   Business:   Finance, Operations, People, Technology, Contracts, Leadership
 * The editor, the matcher, and the skill sheet all share this one list.
 */
export const SKILL_TAGS = [
  'Design', 'Drafting', 'BIM', 'Interiors', 'Structural', 'Site Design', 'Construction', 'Permitting',
  'Project Management', 'Quality Control', 'Client Management', 'Business Development', 'Marketing',
  'Finance', 'Operations', 'People', 'Technology', 'Contracts', 'Leadership',
]

/** Required skill tags for a milestone, inferred from its name/type. May be empty. */
export function requiredSkills(name) {
  const s = String(name || '')
  const tags = []
  for (const [re, tag] of SKILL_RULES) if (re.test(s) && !tags.includes(tag)) tags.push(tag)
  return tags
}

// Activity code (the NATURE of the hours) → the skill(s) it builds. First matching rule wins (an entry
// has exactly one code). Values are arrays — one activity can build several skills (a fee proposal is
// Contracts + Finance + Business Development). Honest separator: reviewing builds Quality Control,
// drafting builds Drafting, regardless of role. (OP- Communication / Marketing have no code-level
// skill — internal chatter isn't a tracked skill, and there's no Marketing code.)
const ACTIVITY_SKILL = [
  [/design work/i,               ['Design']],
  [/drawing production/i,        ['Drafting']],
  [/quality control/i,           ['Quality Control']],
  [/construction correspondence/i, ['Quality Control']],   // desk review of RFIs/submittals/ASIs/COs — still "reviewing"
  [/construction site support/i, ['Construction']],
  [/as-\s*communication/i,       ['Client Management']],   // external (client/consultant); OP- Communication is internal → none
  [/project support/i,           ['Project Management']],
  [/contracts|proposals/i,       ['Contracts', 'Finance', 'Business Development']],
  [/training|development/i,      ['People']],
  [/strategy|planning/i,         ['Leadership', 'Operations']],
  [/it ?& ?software|software/i,  ['Technology']],
  [/administration/i,            ['Operations']],
]
// Entity type → DOMAIN skill(s), always applied (a sheet is always Drafting + its discipline; a billing
// run is Operations + Finance). Deliverables carry no fixed domain — they rely on name keywords.
const ENTITY_TYPE_SKILL = {
  'Drawing Sheet': ['Drafting'],
  'BIM System': ['BIM'],
  'Thread': ['Construction'],
  'Deliverable': [],
  'Agency Review': ['Permitting'],
  'Opportunity': ['Business Development'],
  'Lead': ['Business Development'],
  'Billing Run': ['Operations', 'Finance'],
}
// Phase (the firm CONTEXT) → skill(s). The overhead phases are firm-skill categories; project phases
// get their domain from the entity instead, so only the overhead phases are mapped here.
const PHASE_SKILL = [
  [/billing|invoic/i,          ['Finance', 'Operations']],
  [/business development/i,    ['Business Development']],
  [/general overhead/i,        ['Operations']],
]

const firstStr = v => String((Array.isArray(v) ? v[0] : v) || '')

/**
 * Skill credit a single time entry contributes — the bridge from attributed time → demonstrated skill.
 * Credit follows WHAT WAS DONE, read from three deterministic signals (no AI), unioned + deduped:
 *   1. activity code  → the nature   (Design Work→Design, Documentation→Drafting, …)
 *   2. attributed entity → the domain (name keywords, then an always-on entity-type skill)
 *   3. phase           → firm context (Billing & Invoicing→Finance+Operations, Business Development→BD)
 * The same hours credit EACH mapped skill (concurrent dimensions of one stretch of practice), so role
 * (R/A/S) never weights it. PTO / signal-less entries credit nothing. Pure + node-testable.
 *
 * @param {object} entry { activityCode, entityType, entityName, phaseName, hours }
 * @returns {Array<{ skill, hours }>}
 */
export function skillsForTimeEntry(entry = {}) {
  const hours = Math.max(0, Number(entry.hours) || 0)
  const code = String(entry.activityCode || '')
  if (!hours || /^\s*pto/i.test(code)) return []   // no hours, or PTO → no skill practice

  const tags = []
  const add = t => { const s = String(t || '').trim(); if (s && !tags.includes(s)) tags.push(s) }
  const addAll = arr => (arr || []).forEach(add)

  // 1) Nature — activity code (first matching rule).
  for (const [re, skills] of ACTIVITY_SKILL) if (re.test(code)) { addAll(skills); break }

  // 2) Domain — attributed entity: name keywords + the entity-type skill(s).
  requiredSkills(entry.entityName).forEach(add)
  if (entry.entityType && ENTITY_TYPE_SKILL[entry.entityType]) addAll(ENTITY_TYPE_SKILL[entry.entityType])

  // 3) Firm context — overhead phase.
  const phase = firstStr(entry.phaseName)
  for (const [re, skills] of PHASE_SKILL) if (re.test(phase)) addAll(skills)

  return tags.map(skill => ({ skill, hours }))
}

// Layer weights. Role is the dominant, low-upkeep signal; demonstrated (learned from completed work)
// personalizes it; a manual override is intentional. Inferring skills from the raw role STRING is the
// weak fallback used only when a role has no defined skill set yet.
const ROLE_WEIGHT = 1.5
const DEMO_WEIGHT = 1.2
const MANUAL_WEIGHT = 1.0
const ROLE_INFER_WEIGHT = 0.8

function lookup(map, key) { return map ? (map.get ? map.get(key) : map[key]) : null }
function roleList(person) {
  return String((person.capacity && person.capacity.practiceRoles) || '')
    .split(/[,/]/).map(s => s.trim()).filter(Boolean)
}

/**
 * A person's skill tags → weighted proficiency, blended across the three layers (role-dominant):
 *   - ROLE: the skills defined for the person's role(s) in `ctx.roleSkills` (role → [{skill,proficiency}]),
 *     weighted heavily. If a role has no defined set, fall back to inferring skills from the role string.
 *   - DEMONSTRATED: `ctx.demonstratedByMember` (memberId → [{skill,level}]) learned from completed work.
 *   - MANUAL: the editable `ctx.skillsByMember` per-person overrides.
 * Per skill we keep the strongest weighted signal. ctx fields may be Map or plain object.
 */
export function personSkills(person, ctx = {}) {
  const out = {}
  const bump = (tag, v) => { const t = String(tag || '').trim(); if (t) out[t] = Math.max(out[t] || 0, Math.round(v * 100) / 100) }

  // Role-defined skills (heavy). Fall back to keyword-inferring from the role string per role with no map.
  let anyRoleMap = false
  roleList(person).forEach(role => {
    const defs = lookup(ctx.roleSkills, role)
    if (defs && defs.length) { anyRoleMap = true; defs.forEach(d => bump(d.skill, (Number(d.proficiency) || 3) * ROLE_WEIGHT)) }
  })
  if (!anyRoleMap) {
    const roleStr = roleList(person).join(' ')
    for (const [re, tag] of SKILL_RULES) if (re.test(roleStr)) bump(tag, 3 * ROLE_INFER_WEIGHT)
  }

  // Demonstrated (learned from completed work).
  ;(lookup(ctx.demonstratedByMember, person.memberId) || []).forEach(d => bump(d.skill, (Number(d.level ?? d.proficiency) || 3) * DEMO_WEIGHT))

  // Manual per-person overrides.
  ;(lookup(ctx.skillsByMember, person.memberId) || []).forEach(r => { const p = Number(r.proficiency); bump(r.skill, (Number.isFinite(p) ? p : 3) * MANUAL_WEIGHT) })

  return out
}

// Availability score from the workload status (higher = more room). Overloaded/tight are not targets.
const AVAIL = { light: 3, steady: 2, unknown: 1, tight: 0, overloaded: 0 }

/**
 * Rank eligible candidates to take `milestoneName`. Returns [{ memberId, name, score, eligible,
 * matched, reason }] best-first. `opts.excludeMemberId` drops the current owner (for reassignment).
 *
 * Eligibility: if the milestone requires skills and at least one available person has one, only
 * skill-matched people are eligible; otherwise (no required skill, or nobody matches) eligibility
 * falls back to availability so the system still proposes someone.
 */
const LOAD_BUSY = 1.1   // a target already working at/above this effort load isn't really "free"

export function rankCandidates(milestoneName, people, ctx = {}, opts = {}) {
  const req = requiredSkills(milestoneName)
  // Reconcile the two capacity sensors: the scheduler's status AND the measured effort load. Someone
  // the scheduler thinks has slots but who is logging heavy hours (load >= LOAD_BUSY) is NOT a target.
  const pool = (people || []).filter(p =>
    p.onRoster && p.memberId !== opts.excludeMemberId &&
    p.metrics && p.metrics.status !== 'overloaded' && p.metrics.status !== 'tight' &&
    !(p.metrics.loadRatio != null && Number(p.metrics.loadRatio) >= LOAD_BUSY))

  const scored = pool.map(p => {
    const skills = personSkills(p, ctx)
    const matched = req.filter(t => skills[t])
    const skillScore = matched.reduce((s, t) => s + skills[t], 0)
    const status = (p.metrics && p.metrics.status) || 'unknown'
    // No effort signal at all (no snapshot, nothing active, no scheduler standing) = unknown, not
    // "free". Only flag the light/unknown case — a 'steady' person already has scheduler signal.
    const noData = (status === 'light' || status === 'unknown')
      && (p.metrics && p.metrics.loadRatio == null) && !(p.metrics && p.metrics.activeNow > 0)
    const availScore = (AVAIL[status] != null ? AVAIL[status] : 1)
      + (p.metrics && p.metrics.loadRatio != null ? Math.max(0, 1.2 - Number(p.metrics.loadRatio)) : 0)
      - (noData ? 5 : 0)
    return { person: p, matched, skillScore, availScore, status, noData }
  })

  const anyMatch = req.length > 0 && scored.some(c => c.matched.length > 0)
  const eligible = scored.filter(c => (anyMatch ? c.matched.length > 0 : true))

  eligible.sort((a, b) =>
    (a.noData - b.noData) || (b.skillScore - a.skillScore) || (b.availScore - a.availScore) || (a.person.name || '').localeCompare(b.person.name || ''))

  return eligible.map(c => ({
    memberId: c.person.memberId,
    name: c.person.name,
    score: Math.round((c.skillScore * 2 + c.availScore) * 10) / 10,
    eligible: true,
    matched: c.matched,
    noData: c.noData,
    reason: buildReason(c, req, anyMatch),
  }))
}

function buildReason(c, req, anyMatch) {
  if (c.noData) return 'no recent activity on file — verify before assigning'
  const avail = c.status === 'light' ? 'has capacity' : c.status === 'steady' ? 'available' : c.status
  if (c.matched.length) return `${c.matched.join(' + ')}, ${avail}`
  if (req.length && !anyMatch) return `${avail} (no skill match on file yet)`
  return avail
}

/** Convenience: the single best candidate (or null). */
export function bestCandidate(milestoneName, people, ctx, opts) {
  return rankCandidates(milestoneName, people, ctx, opts)[0] || null
}

/**
 * A person's skill SHEET for display (the RPG character sheet) — the un-weighted 0–5 level per skill,
 * taken as the strongest of the three layers, with which layers contributed. Distinct from
 * personSkills (which weights layers for ranking); this is "what is this person's actual level."
 * Returns [{ skill, level, sources:[role|learned|manual], role, demo, manual }] best-first.
 */
export function personSkillSheet(person, ctx = {}) {
  const role = {}, demo = {}, manual = {}
  const bump = (m, k, v) => { const t = String(k || '').trim(); if (t) m[t] = Math.max(m[t] || 0, Number(v) || 0) }

  let anyRoleMap = false
  roleList(person).forEach(r => {
    const defs = lookup(ctx.roleSkills, r)
    if (defs && defs.length) { anyRoleMap = true; defs.forEach(d => bump(role, d.skill, d.proficiency)) }
  })
  if (!anyRoleMap) { const s = roleList(person).join(' '); for (const [re, tag] of SKILL_RULES) if (re.test(s)) bump(role, tag, 3) }
  ;(lookup(ctx.demonstratedByMember, person.memberId) || []).forEach(d => bump(demo, d.skill, d.level ?? d.proficiency))
  ;(lookup(ctx.skillsByMember, person.memberId) || []).forEach(r => bump(manual, r.skill, r.proficiency))

  // Demonstrated-first: the sheet shows what a person has actually BUILT (logged-work evidence), with
  // the role expectation as a reference tick — not a max-blend, which let an all-roles principal read as
  // a 5.0 at everything and buried the demonstrated signal. Skills the role grants but with no logged
  // evidence drop to a muted "role baseline" list (eligibility, not evidence). Manual overrides feed the
  // assignment engine elsewhere; they're intentionally NOT surfaced here (edited behind the Edit panel).
  const round = v => Math.round((Number(v) || 0) * 10) / 10
  const skills = new Set([...Object.keys(role), ...Object.keys(demo), ...Object.keys(manual)])
  const demonstrated = [], roleOnly = []
  skills.forEach(skill => {
    const d = demo[skill] || 0, r = role[skill] || 0
    if (d > 0) demonstrated.push({ skill, level: round(d), role: round(r), manual: round(manual[skill] || 0) })
    else if (r > 0) roleOnly.push({ skill, role: round(r) })
  })
  demonstrated.sort((a, b) => b.level - a.level || a.skill.localeCompare(b.skill))
  roleOnly.sort((a, b) => b.role - a.role || a.skill.localeCompare(b.skill))
  return { demonstrated, roleOnly }
}
